import api from "@config/api";
import {action, computed, observable} from "mobx";
import colors from "@constants/colors";
import _ from "lodash";
import {BaseModel, BaseStore} from "../BaseStore";
import cpStore from "./cpStore";
import sessionStore from "../sessionStore";
import languageStore, {LanguageDialect, LanguageModel} from "../languageStore";
import sessionTypeStore from "@stores/domain/sessionTypeStore";


const service = {
  getLanguages: (): Promise => api.get("languages"),
  getLanguage: (id): Promise => api.get(`languages/${id}`),
  updateLanguage: ({id, data}): Promise => api.post(`languages/${id}`, data),
};

export class StudentLanguageModel extends BaseModel {
  loader = service.getLanguage;

  updater = service.updateLanguage;

  deleter = service.deleteLanguage;

  parentStore = store;

  endPoint = "languages";

  @observable
  cpId;

  @observable
  id;


  @observable
  level;

  @observable
  dialectId;

  @observable
  creditUsed;

  @observable
  color: string = "black";

  @observable
  enabledTypes = [];

  @observable
  completedSessionHours = 0;

  @observable
  totalSessionHours = 0;

  @observable
  pendingSessionHours = 0;

  @computed
  get cp() {
    if (this.cpId)
      return cpStore.findOrAdd(this.cpId);
    return null;
  }


  @computed
  get defaultType(){
    return this.plainDialect.name !== "loading"?this.plainDialect.enabledTypes.length>0?sessionTypeStore.find(this.plainDialect.enabledTypes[0].id):null:null;
  }

  @computed
  get name() {
    const language = languageStore.findByDialect(this.dialectId);
    return language ? language.name : "loading";
  }

  @computed
  get plainDialect(): ?LanguageDialect {
    const dialect = languageStore.findDialect(this.dialectId);

    return dialect || {id: 0, name: "loading"};
  }

  @computed
  get dialect(): string {
    return this.plainDialect ? this.plainDialect.name : "loading";
  }

  @computed
  get plainLanguage(): ?LanguageModel {
    const language = languageStore.findByDialect(this.dialectId);

    return language || {id: 0, name: "loading"};
  }

  @computed
  get language(): string {
    return this.plainLanguage.name;
  }


  @computed
  get sessions() {
    return sessionStore.data.filter(s => s.studentLanguageId === this.id);
  }

  @computed
  get completedSessions() {
    return this.sessions.filter(s => s.isCompleted === true);
  }

  @computed
  get activeSessions() {
    return this.sessions.filter(s => s.isCompleted === false);
  }

  @computed
  get hoursCompleted() {
    return _.sumBy(this.completedSessions, s => s.duration) / 60;
  }

  @action
  addPendingSessionHours(duration){
    const d= duration /60;
    this.pendingSessionHours +=d;
    this.totalSessionHours +=d;
  }

  @action
  subPendingSessionHours(duration){
    const d = duration/60;
    this.pendingSessionHours -=d;
    this.totalSessionHours -=d;
  }

  @action
  addCreditUsed(v){
    this.creditUsed += v;
  }

  @action
  subCreditUsed(v){
    this.creditUsed -= v;
  }

  constructor(args, index) {
    super(args);
    this.color = colors.gradient[index % 5];
    sessionStore.consume(args.activeSessions, this.id);
  }

  @action
  serializeData(data) {
    this.id = data.id;
    this.level = data.level.name;
    this.dialectId = data.languageDialect.id;
    this.enabledTypes = data.enabledTypes;
    this.completedSessionHours = data.completedSessionHours;
    this.totalSessionHours = data.totalSessionHours;
    this.pendingSessionHours = data.pendingSessionHours;
    this.focusOn = data.focusOn;
    this.lookingFor = data.lookingFor;
    this.whatToCover = data.whatToCover;
    this.additionalRequests = data.additionalRequests;
    this.creditUsed = data.creditUsed;

    if (data.cp && typeof data.cp !== "number") {
      cpStore.findOrAdd({...data.cp.user,sessionAvailabilities:data.cp.sessionAvailabilities}).dialectId = this.dialectId;
      this.cpId = data.cp.user.id;
    }
  }
}

export class StudentLanguageStore extends BaseStore {

  loader = service.getLanguages;

  DataModel = StudentLanguageModel;

  @observable
  allData = [];

  @computed
  get isEmpty(){
    return this.allData.length <1;
  }



  @action
  consume(data: [Object]) {
    const filteredData = data.filter(d => d.cp !== null);
    filteredData.map(d => this.data.push(new StudentLanguageModel(d, this.data.length)));

    this.allData = [...this.data];
    const otherData = data.filter(d=>d.cp === null);
    otherData.map(d => this.allData.push(new StudentLanguageModel(d, this.allData.length)));
    this.react();
  }

  @action.bound
  clear() {
    this.data = [];
    this.allData = [];
  }

  @computed
  get completedSessionHours(){
    return _.sumBy(this.data,d=>d.completedSessionHours);
  }

  @computed
  get totalSessionHours(){
    return _.sumBy(this.data,d=>d.totalSessionHours);
  }

  @computed
  get pendingSessionHours(){
    return _.sumBy(this.data,d=>d.pendingSessionHours);
  }

  findByDialect(id){
    return this.allData.find(f=>f.dialectId === id);
  }

  @action.bound
  add(ob){
    this.allData.push(new this.DataModel(ob));
  }

}

const store = new StudentLanguageStore();
window.studentLanguageStore = store;

export default store;
